'use client';

import { useState, useEffect } from 'react';

type QueryParamValue = string | number | undefined | null;

type QueryParams = Record<string, QueryParamValue>;

export interface UtmParams extends QueryParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  gclid?: string;
  msclkid?: string;
  fbclid?: string;
}

interface TrackedSellMyCarLinkOptions {
  ctaSource: string;
  params?: QueryParams;
  utmParams?: UtmParams;
  fallbackUtmParams?: UtmParams;
}

const DEFAULT_INTERNAL_UTMS = {
  utm_source: 'thiscar',
  utm_medium: 'website',
  utm_campaign: 'inventory_trade_appraisal'
} as const;

export function readUtmParamsFromCookie(cookieString: string): UtmParams {
  try {
    const cookies = cookieString.split(';');
    const utmCookie = cookies.find((cookie) => cookie.trim().startsWith('tc_utm='));

    if (!utmCookie) {
      return {};
    }

    const separatorIndex = utmCookie.indexOf('=');
    if (separatorIndex === -1) {
      return {};
    }

    const value = utmCookie.slice(separatorIndex + 1);
    if (!value) {
      return {};
    }

    const decoded = decodeURIComponent(value);
    return JSON.parse(decoded) as UtmParams;
  } catch (error) {
    console.error('Error parsing UTM cookie:', error);
    return {};
  }
}

function readBrowserCookieString(): string {
  if (typeof document === 'undefined') {
    return '';
  }

  return document.cookie || '';
}

export function withInternalUtmFallback(
  params: UtmParams = {},
  fallbackParams: UtmParams = DEFAULT_INTERNAL_UTMS
): UtmParams {
  const usesFallbackAttribution = !params.utm_source && !params.utm_medium && !params.utm_campaign;
  const content = params.utm_content || (usesFallbackAttribution ? fallbackParams.utm_content : undefined);
  const term = params.utm_term || (usesFallbackAttribution ? fallbackParams.utm_term : undefined);

  return {
    utm_source: params.utm_source || fallbackParams.utm_source || DEFAULT_INTERNAL_UTMS.utm_source,
    utm_medium: params.utm_medium || fallbackParams.utm_medium || DEFAULT_INTERNAL_UTMS.utm_medium,
    utm_campaign: params.utm_campaign || fallbackParams.utm_campaign || DEFAULT_INTERNAL_UTMS.utm_campaign,
    ...(content ? { utm_content: content } : {}),
    ...(term ? { utm_term: term } : {}),
    ...(params.gclid ? { gclid: params.gclid } : {}),
    ...(params.msclkid ? { msclkid: params.msclkid } : {}),
    ...(params.fbclid ? { fbclid: params.fbclid } : {})
  };
}

function appendQueryParams(searchParams: URLSearchParams, params: QueryParams) {
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }

    const normalized = typeof value === 'number' ? value.toString() : value.trim();
    if (!normalized) {
      return;
    }

    searchParams.set(key, normalized);
  });
}

export function buildTrackedSellMyCarUrl({
  ctaSource,
  params = {},
  utmParams = {},
  fallbackUtmParams = DEFAULT_INTERNAL_UTMS
}: TrackedSellMyCarLinkOptions): string {
  const searchParams = new URLSearchParams();

  appendQueryParams(searchParams, params);
  searchParams.set('cta_source', ctaSource);
  appendQueryParams(searchParams, withInternalUtmFallback(utmParams, fallbackUtmParams));

  return `/sell-my-car?${searchParams.toString()}`;
}

/**
 * Hook to read UTM parameters from the tc_utm cookie
 * This cookie is set by middleware and is client-readable for third-party form attribution
 */
export function useUtmParams(): UtmParams {
  // Match the server's initial render, then apply browser-only attribution.
  // Reading cookies in the initializer creates a hydration mismatch that can
  // leave an anchor's server-rendered (unattributed) href unchanged.
  const [utmParams, setUtmParams] = useState<UtmParams>({});

  useEffect(() => {
    setUtmParams(readUtmParamsFromCookie(readBrowserCookieString()));
  }, []);

  return utmParams;
}

/**
 * Build a URL query string from UTM params
 * Only includes params that have values
 */
export function buildUtmQueryString(params: UtmParams): string {
  const searchParams = new URLSearchParams();
  appendQueryParams(searchParams, params);
  return searchParams.toString();
}

/**
 * Append UTM params to a URL
 */
export function appendUtmToUrl(baseUrl: string, params: UtmParams): string {
  const queryString = buildUtmQueryString(params);
  if (!queryString) return baseUrl;

  const separator = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${separator}${queryString}`;
}
